import { X, Clock, Users, Star, ChefHat, Sparkles } from "lucide-react";
import type { AiRecipe } from "../types";
import { servingsLabel, type Language } from "../data";
import { MacroDonut } from "./MacroDonut";

interface AiRecipeCardProps {
  recipe: AiRecipe;
  onClose: () => void;
  t: (k: string) => string;
  dir: "rtl" | "ltr";
  lang: Language;
}

export function AiRecipeCard({ recipe, onClose, t, dir, lang }: AiRecipeCardProps) {
  return (
    <div className="rounded-2xl border-2 border-violet-200 dark:border-violet-500/30 overflow-hidden shadow-md" dir={dir}>
      <div className="bg-gradient-to-r from-violet-600 to-purple-500 px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="flex items-center gap-1.5 bg-white/20 text-white text-xs font-semibold px-2.5 py-1 rounded-full">
            <Sparkles className="w-3 h-3" />
            {t("aiRecipeTitle")}
          </span>
          {recipe.cached && (
            <span className="text-white/80 text-xs flex items-center gap-1">⚡ {t("aiFromCache")}</span>
          )}
        </div>
        <button onClick={onClose} className="p-1 rounded-full text-white/70 hover:text-white hover:bg-white/20 transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="bg-white dark:bg-slate-800 px-5 pt-4 pb-2">
        <h3 className="text-xl font-bold text-stone-800 dark:text-slate-100">{recipe.name}</h3>
        <p className="text-xs text-stone-400 dark:text-slate-500 mt-0.5">{t("aiDisclaimer")}</p>
      </div>

      <div className="grid grid-cols-2 gap-3 px-5 py-2 bg-white dark:bg-slate-800">
        <div className="flex items-center gap-2 bg-green-50 dark:bg-green-600/15 rounded-xl p-2.5">
          <Clock className="w-4 h-4 text-green-600 dark:text-green-400 shrink-0" />
          <span className="text-sm text-stone-700 dark:text-slate-200">{recipe.time} {t("minutes")}</span>
        </div>
        <div className="flex items-center gap-2 bg-green-50 dark:bg-green-500/15 rounded-xl p-2.5">
          <Users className="w-4 h-4 text-green-600 dark:text-green-400 shrink-0" />
          <span className="text-sm text-stone-700 dark:text-slate-200">{servingsLabel(lang, recipe.servings)}</span>
        </div>
      </div>

      {recipe.ingredients_list && recipe.ingredients_list.length > 0 && (
        <div className="px-5 py-4 bg-white dark:bg-slate-800 border-t border-stone-100 dark:border-slate-700">
          <h4 className="font-bold text-stone-800 dark:text-slate-100 mb-3 flex items-center gap-2 text-sm">
            <Star className="w-4 h-4 text-green-500" />
            {t("aiIngredientsList")}
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {recipe.ingredients_list.map((ing, i) => (
              <div key={i} className="flex items-center justify-between bg-green-50 dark:bg-green-500/10 rounded-xl px-3 py-2.5 gap-3 border border-green-100 dark:border-green-500/20">
                <span className="text-sm font-medium text-stone-800 dark:text-slate-100 leading-snug">{ing.name}</span>
                <span className="text-xs font-bold text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-500/25 px-2.5 py-1 rounded-full whitespace-nowrap shrink-0">{ing.quantity}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {recipe.nutrition && (
        <div className="px-5 py-4 bg-white dark:bg-slate-800 border-t border-stone-100 dark:border-slate-700">
          <MacroDonut
            calories={recipe.nutrition.calories}
            protein={recipe.nutrition.protein}
            carbs={recipe.nutrition.carbs}
            fat={recipe.nutrition.fat}
            sugar={recipe.nutrition.sugar}
            fiber={recipe.nutrition.fiber ?? 0}
            sodium={recipe.nutrition.sodium ?? 0}
            t={t}
            dir={dir}
          />
        </div>
      )}

      <div className="px-5 pt-3 pb-5 bg-white dark:bg-slate-800 border-t border-stone-100 dark:border-slate-700">
        <h4 className="font-bold text-stone-800 dark:text-slate-100 mb-3 flex items-center gap-2 text-sm">
          <ChefHat className="w-4 h-4 text-violet-500 dark:text-violet-400" />
          {t("steps")}
        </h4>
        <ol className="space-y-2.5">
          {recipe.steps.map((step, i) => (
            <li key={i} className="flex gap-3">
              <span className="flex-shrink-0 w-5 h-5 bg-violet-500 text-white rounded-full text-[10px] flex items-center justify-center font-bold">{i + 1}</span>
              <p className="text-stone-700 dark:text-slate-300 text-sm leading-relaxed">{step}</p>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
